import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { monthLabel, stateLabel, useGstMonths } from '@/lib/gst';
import { useOrgId } from '@/lib/org';
import { useOrgLocale } from '@/lib/org-locale';
import { useCompanySettings } from '@/lib/settings';

export const Route = createFileRoute('/taxes/gst/')({ component: GstCentre });

const REPORTS: Array<[string, string]> = [
  ['gstr-1', 'GSTR-1'],
  ['gstr-3b', 'GSTR-3B'],
  ['gst-detail', 'GST detail'],
  ['itc-summary', 'Input tax credit summary'],
  ['hsn-summary', 'HSN/SAC summary'],
];

/** Screen T6 (document 06): Taxes › GST for India clients, one row per month. */
function GstCentre() {
  const orgId = useOrgId();
  const { taxPack, currency } = useOrgLocale();
  const months = useGstMonths();
  const settings = useCompanySettings();
  const gst = settings.data?.gst;

  return (
    <>
      <PageHeader
        title="GST"
        description="GSTR-1 and GSTR-3B for each month, computed from the books."
        actions={
          taxPack === 'in' && orgId ? (
            <>
              <Button asChild variant="outline">
                <Link to="/taxes/gst/ims">IMS and GSTR-2B</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/taxes/gst/eway-bills">E-way bills</Link>
              </Button>
            </>
          ) : undefined
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : taxPack !== 'in' ? (
        <EmptyState
          title="GST applies to India clients"
          hint="This client files UAE VAT returns."
          action={
            <Button asChild variant="outline">
              <Link to="/taxes/vat">VAT</Link>
            </Button>
          }
        />
      ) : months.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : months.isError ? (
        <EmptyState title="Could not load GST returns" hint={months.error.message} />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 rounded-lg border bg-card px-4 py-3 text-sm">
            <span>
              <span className="text-muted-foreground">GSTIN </span>
              <span className="tnum font-medium">
                {months.data.gstin ?? 'Not registered: add the GSTIN in Company settings'}
              </span>
            </span>
            <span>
              <span className="text-muted-foreground">State </span>
              <span className="font-medium">{stateLabel(months.data.stateCode)}</span>
            </span>
            <span>
              <span className="text-muted-foreground">Filing frequency </span>
              <span className="font-medium">
                {gst?.taxPeriod === 'quarterly' ? 'Quarterly' : 'Monthly'}
              </span>
            </span>
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead className="w-[90px] text-right">Documents</TableHead>
                  <TableHead className="w-[150px] text-right">Outward taxable</TableHead>
                  <TableHead className="w-[130px] text-right">Output tax</TableHead>
                  <TableHead className="w-[130px] text-right">Input tax credit</TableHead>
                  <TableHead className="w-[140px] text-right">Cash payable</TableHead>
                  <TableHead className="w-[150px]">Due</TableHead>
                  <TableHead className="w-[200px] text-right">Returns</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {months.data.months.map((m) => (
                  <TableRow key={m.period} data-testid="gst-month">
                    <TableCell>
                      <div className="font-medium">{monthLabel(m.period)}</div>
                      {m.findings ? (
                        <div className="inline-flex items-center gap-1 text-xs text-[#B45309]">
                          <AlertTriangle className="size-3.5" aria-hidden />
                          {m.findings} exception{m.findings === 1 ? '' : 's'}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="tnum text-right">{m.documents}</TableCell>
                    <TableCell className="tnum text-right">
                      {formatAmount(m.outwardTaxable)}
                    </TableCell>
                    <TableCell className="tnum text-right">{formatAmount(m.outputTax)}</TableCell>
                    <TableCell className="tnum text-right">{formatAmount(m.itc)}</TableCell>
                    <TableCell className="tnum text-right font-medium">
                      {currency} {formatAmount(m.cashPayable)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>GSTR-1 {displayDate(m.gstr1Due)}</div>
                      <div>GSTR-3B {displayDate(m.gstr3bDue)}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="link" className="px-2">
                        <Link to="/taxes/gst/gstr1" search={{ period: m.period }}>
                          GSTR-1
                        </Link>
                      </Button>
                      <Button asChild size="sm" variant="link" className="px-2">
                        <Link to="/taxes/gst/gstr3b" search={{ period: m.period }}>
                          GSTR-3B
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <h2 className="mt-6 mb-2 text-base font-semibold">GST reports</h2>
          <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {REPORTS.map(([key, label]) => (
              <li key={key}>
                <Link
                  to="/reports/$reportKey"
                  params={{ reportKey: key }}
                  className="text-primary hover:underline"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
